/**
 * Claims Analytics tab (Benefits → Claims). HR-admin only.
 *
 * Upload a carrier claims report → de-identified parse + AI analysis →
 * dashboard of spend by diagnosis, drug spend, utilization, a GLP-1
 * spotlight, and the AI findings. Everything shown is aggregate.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Lightbulb,
  Pill,
  ShieldCheck,
  TrendingUp,
  Upload,
} from "lucide-react";
import { pageTransition, formatCurrency } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  analyzeClaims,
  compareClaims,
  createShareLink,
  downloadClaimsPdf,
  getClaims,
  listClaims,
  uploadClaims,
  type ClaimsComparison,
  type ClaimsReport,
  type ClaimsReportDetail,
} from "@/api/claims";

function Bar({ pct, label, value }: { pct: number; label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-text-primary truncate">{label}</span>
        <span className="text-text-muted ml-2 shrink-0">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-background overflow-hidden">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card variant="flat" className="p-4">
      <p className="text-[11px] uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold font-heading text-text-primary">
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </Card>
  );
}

function UploadModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (id: string) => void;
}) {
  const { addToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [carrier, setCarrier] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [dataType, setDataType] = useState("medical");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) {
      addToast("error", "Choose a CSV or XLSX file");
      return;
    }
    setBusy(true);
    try {
      const res = await uploadClaims({
        file,
        carrier_name: carrier,
        report_period_start: start,
        report_period_end: end,
        data_type: dataType,
      });
      if (res.needs_mapping?.length) {
        addToast(
          "error",
          "Couldn't detect a claim-amount column — check the file headers.",
        );
      } else {
        addToast("success", "Uploaded — running analysis…");
        onUploaded(res.claims.id);
        onClose();
      }
    } catch {
      addToast("error", "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Upload claims report">
      <div className="space-y-3">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-accent/10 file:px-3 file:py-2 file:text-accent"
        />
        <Input
          label="Carrier name"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          placeholder="Blue Cross Blue Shield"
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Period start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <Input
            label="Period end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <Select
          label="Data type"
          value={dataType}
          onChange={(e) => setDataType(e.target.value)}
          options={[
            { value: "medical", label: "Medical" },
            { value: "dental", label: "Dental" },
            { value: "vision", label: "Vision" },
            { value: "pharmacy", label: "Pharmacy" },
          ]}
        />
        <p className="flex items-start gap-2 text-xs text-text-muted">
          <ShieldCheck className="h-4 w-4 text-success shrink-0 mt-0.5" />
          This data is encrypted at rest, de-identified for analysis
          (member IDs are hashed, never stored in the clear), and never
          used to train AI models.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={busy}>
            <Upload className="h-4 w-4" /> Upload &amp; analyze
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function ClaimsAnalyticsTab() {
  const { addToast } = useToast();
  const [reports, setReports] = useState<ClaimsReport[]>([]);
  const [selected, setSelected] = useState<ClaimsReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [compareWith, setCompareWith] = useState("");
  const [comparison, setComparison] = useState<ClaimsComparison | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listClaims();
      setReports(list);
      if (list.length > 0) {
        const detail = await getClaims(list[0].id);
        setSelected(detail);
      } else {
        setSelected(null);
      }
    } catch {
      addToast("error", "Couldn't load claims reports");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const runAnalysis = async (id: string) => {
    setAnalyzing(true);
    try {
      const res = await analyzeClaims(id);
      const detail = await getClaims(id);
      setSelected(detail);
      if (res.status === "error" || res.error) {
        addToast("error", res.error || "Analysis failed");
      } else {
        addToast("success", "Analysis complete");
      }
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Analysis failed";
      addToast("error", detail);
    } finally {
      setAnalyzing(false);
    }
  };

  const onUploaded = async (id: string) => {
    await loadList();
    await runAnalysis(id);
  };

  const handleShare = async () => {
    if (!selected) return;
    setSharing(true);
    try {
      const res = await createShareLink(selected.id);
      setShareUrl(res.url);
      try {
        await navigator.clipboard.writeText(res.url);
        addToast("success", "Share link copied — valid 7 days");
      } catch {
        addToast("success", "Share link created — valid 7 days");
      }
    } catch {
      addToast("error", "Couldn't create share link");
    } finally {
      setSharing(false);
    }
  };

  const handleCompare = async (otherId: string) => {
    setCompareWith(otherId);
    setComparison(null);
    if (!selected || !otherId) return;
    try {
      // selected = later report; otherId = earlier (or vice versa) —
      // backend treats a=earlier, b=later. Pass other as earlier.
      const cmp = await compareClaims(otherId, selected.id);
      setComparison(cmp);
    } catch {
      addToast("error", "Couldn't compare reports");
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  // Empty state.
  if (reports.length === 0) {
    return (
      <>
        <Card className="text-center py-12">
          <TrendingUp className="h-8 w-8 text-accent mx-auto mb-2" />
          <p className="text-text-primary font-medium">
            Unlock population health &amp; spend insights
          </p>
          <p className="text-sm text-text-muted mt-1 max-w-md mx-auto">
            Upload your carrier claims report and the AI surfaces spend
            patterns, diagnosis trends, GLP-1 utilization, and renewal
            recommendations. Your data is encrypted and never shared.
          </p>
          <Button className="mt-4" onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4" /> Upload claims report
          </Button>
        </Card>
        <UploadModal
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onUploaded={onUploaded}
        />
      </>
    );
  }

  const stats = selected?.analysis_result?.stats;
  const ai = selected?.analysis_result?.ai;

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Select
            value={selected?.id ?? ""}
            onChange={async (e) => setSelected(await getClaims(e.target.value))}
            options={reports.map((r) => ({
              value: r.id,
              label: `${r.carrier_name || "Claims"} · ${r.report_period_start ?? "?"}→${r.report_period_end ?? "?"} (${r.status})`,
            }))}
            className="w-80"
          />
          {selected && selected.status !== "analyzed" && (
            <Button
              size="sm"
              onClick={() => runAnalysis(selected.id)}
              isLoading={analyzing}
            >
              Analyze
            </Button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected?.status === "analyzed" && (
            <>
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    await downloadClaimsPdf(selected.id);
                  } catch {
                    addToast("error", "Couldn't export the PDF");
                  }
                }}
              >
                Export PDF
              </Button>
              <Button variant="secondary" onClick={handleShare} isLoading={sharing}>
                Share with broker
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4" /> Upload
          </Button>
        </div>
      </div>

      {shareUrl && (
        <Card className="border-accent/30 bg-accent/5">
          <p className="text-xs text-text-muted mb-1">
            Read-only broker link (expires in 7 days, revocable, aggregate
            only):
          </p>
          <code className="text-xs break-all text-text-primary">{shareUrl}</code>
        </Card>
      )}

      {/* Period-over-period comparison */}
      {reports.length > 1 && selected?.status === "analyzed" && (
        <Card>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <h3 className="text-sm font-semibold text-text-primary">
              Compare with
            </h3>
            <Select
              value={compareWith}
              onChange={(e) => handleCompare(e.target.value)}
              options={[
                { value: "", label: "— Select an earlier report —" },
                ...reports
                  .filter((r) => r.id !== selected.id && r.status === "analyzed")
                  .map((r) => ({
                    value: r.id,
                    label: `${r.carrier_name || "Claims"} · ${r.report_period_start ?? "?"}→${r.report_period_end ?? "?"}`,
                  })),
              ]}
              className="w-72"
            />
          </div>
          {comparison && (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap text-sm">
                <Badge
                  variant={comparison.total_spend_delta > 0 ? "danger" : "success"}
                >
                  Total spend {comparison.total_spend_delta >= 0 ? "+" : ""}
                  {formatCurrency(comparison.total_spend_delta)}
                </Badge>
                <Badge
                  variant={comparison.pmpm_delta > 0 ? "danger" : "success"}
                >
                  PMPM {comparison.pmpm_delta >= 0 ? "+" : ""}
                  {formatCurrency(comparison.pmpm_delta)}
                </Badge>
                {comparison.glp1_delta !== 0 && (
                  <Badge variant="info">
                    GLP-1 {comparison.glp1_delta >= 0 ? "+" : ""}
                    {formatCurrency(comparison.glp1_delta)}
                  </Badge>
                )}
              </div>
              <div className="space-y-1">
                {comparison.category_deltas.slice(0, 8).map((c) => (
                  <div
                    key={c.category}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-text-primary">{c.category}</span>
                    <span
                      className={
                        c.direction === "up"
                          ? "text-danger"
                          : c.direction === "down"
                            ? "text-success"
                            : "text-text-muted"
                      }
                    >
                      {c.delta >= 0 ? "+" : ""}
                      {formatCurrency(c.delta)}
                      {c.pct_change != null ? ` (${c.pct_change >= 0 ? "+" : ""}${c.pct_change}%)` : ""}
                    </span>
                  </div>
                ))}
              </div>
              {comparison.new_categories.length > 0 && (
                <p className="text-xs text-text-muted">
                  New this period:{" "}
                  <span className="text-text-primary">
                    {comparison.new_categories.join(", ")}
                  </span>
                </p>
              )}
            </div>
          )}
        </Card>
      )}

      {selected?.status === "error" && (
        <Card>
          <p className="flex items-center gap-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4" />
            {selected.error_message || "This report couldn't be processed."}
          </p>
        </Card>
      )}

      {!stats ? (
        <Card className="text-center py-10">
          <p className="text-text-muted text-sm">
            {analyzing
              ? "Analyzing claims…"
              : "Run analysis to see insights for this report."}
          </p>
        </Card>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Total spend" value={formatCurrency(stats.total_spend)} />
            <Stat
              label="Per member / month"
              value={formatCurrency(stats.pmpm)}
              sub="national avg ~$500-600"
            />
            <Stat label="Total claims" value={stats.total_claims.toLocaleString()} />
            <Stat
              label="Members"
              value={String(stats.total_members)}
              sub={stats.period ?? undefined}
            />
          </div>

          {/* Spend by diagnosis + drug spend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <h3 className="text-sm font-semibold text-text-primary mb-3">
                Spend by diagnosis
              </h3>
              <div className="space-y-3">
                {stats.by_diagnosis.slice(0, 8).map((d) => (
                  <Bar
                    key={d.category}
                    label={d.category}
                    value={`${formatCurrency(d.spend)} (${d.pct}%)`}
                    pct={d.pct}
                  />
                ))}
              </div>
            </Card>
            <Card>
              <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Pill className="h-4 w-4" /> Drug spend
              </h3>
              <div className="space-y-3">
                {stats.by_drug.length === 0 && (
                  <p className="text-sm text-text-muted">No Rx claims found.</p>
                )}
                {(() => {
                  const max = Math.max(1, ...stats.by_drug.map((d) => d.spend));
                  return stats.by_drug.map((d) => (
                    <Bar
                      key={d.category}
                      label={d.category}
                      value={formatCurrency(d.spend)}
                      pct={(d.spend / max) * 100}
                    />
                  ));
                })()}
              </div>
            </Card>
          </div>

          {/* GLP-1 spotlight */}
          {stats.glp1_spend > 0 && (
            <Card className="border-accent/30 bg-accent/5">
              <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-2">
                <Pill className="h-4 w-4 text-accent" /> GLP-1 spotlight
              </h3>
              <p className="text-sm text-text-muted">
                GLP-1 medications account for{" "}
                <strong className="text-text-primary">
                  {formatCurrency(stats.glp1_spend)}
                </strong>{" "}
                of your drug spend
                {stats.rx_spend > 0
                  ? ` (${Math.round((stats.glp1_spend / stats.rx_spend) * 100)}% of total Rx)`
                  : ""}
                , across{" "}
                <strong className="text-text-primary">
                  {stats.glp1_members}
                </strong>{" "}
                member{stats.glp1_members === 1 ? "" : "s"}.
                {ai?.glp1_analysis?.recommendation
                  ? ` ${ai.glp1_analysis.recommendation}`
                  : " Consider a structured weight-management program to complement pharmaceutical intervention."}
              </p>
            </Card>
          )}

          {/* Utilization */}
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-3">
              Utilization by provider type
            </h3>
            <div className="space-y-3">
              {(() => {
                const max = Math.max(
                  1,
                  ...stats.utilization_mix.map((u) => u.visits),
                );
                return stats.utilization_mix.slice(0, 8).map((u) => (
                  <Bar
                    key={u.provider_type}
                    label={u.provider_type}
                    value={`${u.visits} visits · ${formatCurrency(u.spend)}`}
                    pct={(u.visits / max) * 100}
                  />
                ));
              })()}
            </div>
          </Card>

          {/* AI findings */}
          {ai && !ai.error && (
            <div className="space-y-4">
              {ai.top_findings && ai.top_findings.length > 0 && (
                <Card>
                  <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-warning" /> Top findings
                  </h3>
                  <ol className="space-y-2 list-decimal ml-4">
                    {ai.top_findings.map((f, i) => (
                      <li key={i} className="text-sm">
                        <span className="text-text-primary font-medium">
                          {f.title}
                        </span>
                        {f.financial_impact && (
                          <Badge variant="info" className="ml-2">
                            {f.financial_impact}
                          </Badge>
                        )}
                        <p className="text-text-muted mt-0.5">{f.detail}</p>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
              {ai.risk_areas && ai.risk_areas.length > 0 && (
                <Card>
                  <h3 className="text-sm font-semibold text-text-primary mb-3">
                    Risk areas
                  </h3>
                  <ul className="space-y-2">
                    {ai.risk_areas.map((r, i) => (
                      <li key={i} className="text-sm">
                        <span className="text-text-primary font-medium">
                          {r.area}
                        </span>
                        <p className="text-text-muted mt-0.5">{r.detail}</p>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {ai.program_recommendations &&
                ai.program_recommendations.length > 0 && (
                  <Card>
                    <h3 className="text-sm font-semibold text-text-primary mb-3">
                      Program recommendations
                    </h3>
                    <ul className="space-y-2">
                      {ai.program_recommendations.map((p, i) => (
                        <li key={i} className="text-sm">
                          <span className="text-text-primary font-medium">
                            {p.program}
                          </span>
                          {p.est_impact && (
                            <Badge variant="success" className="ml-2">
                              {p.est_impact}
                            </Badge>
                          )}
                          <p className="text-text-muted mt-0.5">{p.rationale}</p>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              {(ai.renewal_impact || ai.benchmarks) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {ai.renewal_impact && (
                    <Card>
                      <h3 className="text-sm font-semibold text-text-primary mb-2">
                        Renewal impact
                      </h3>
                      <p className="text-sm text-text-muted">
                        {ai.renewal_impact}
                      </p>
                    </Card>
                  )}
                  {ai.benchmarks && (
                    <Card>
                      <h3 className="text-sm font-semibold text-text-primary mb-2">
                        Benchmarks
                      </h3>
                      <p className="text-sm text-text-muted">
                        {ai.benchmarks.pmpm_vs_national}{" "}
                        {ai.benchmarks.notes}
                      </p>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <UploadModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        onUploaded={onUploaded}
      />
    </motion.div>
  );
}
